
import * as React from "react";
import { Helmet } from "react-helmet-async";
import { PaletteMode } from "@mui/material";
import CssBaseline from "@mui/material/CssBaseline";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import AppAppBar from "../../components/layout/AppAppBar";
import Hero from "../../components/landing/Hero";
import FAQ from "../../components/landing/FAQ";
import HowItWorks from "../../components/landing/HowItWorks";
import Footer from "../../components/layout/Footer";
import getLPTheme from "../../getLPTheme";
import Testimonials from "../../components/Testimonials";
import OfferSection from "../../components/offers/OfferSection";
import CounterSection from "../../components/landing/Counter";
import TaskSteps from "../../components/common/TaskSteps";
import PartnersSection from "../../components/landing/PartnersSection";
import PayBanner from "../../components/common/PayBanner";


function Floater() {
  return (
    <Box
      component="a"
      href="https://drive.google.com/file/d/1EkfdPWYSJeneO05bBNX3EStQqqtsfdNN/view?usp=sharing"
      target="_blank"
      rel="noopener noreferrer"
      sx={{
        position: "fixed",
        bottom: "20px",
        right: "20px",
        zIndex: 1000,
        "& img": {
          width: "180px",
          height: "auto",
          "@media (max-width: 600px)": {
            width: "120px",
          },
          "@media (max-width: 400px)": {
            width: "90px",
          },
        },
      }}
    >
      <img
        src="https://github.com/appversal/filehost/blob/main/downloadicon.png?raw=true"
        alt="Special Offer"
        data-track="image"
        data-name="landing_floater_icon"
      />
    </Box>
  );
}

export default function LandingPage() {
  const [mode, setMode] = React.useState<PaletteMode>("light");
  const [showCustomTheme] = React.useState(true);
  const LPtheme = createTheme(getLPTheme(mode));
  const defaultTheme = createTheme({ palette: { mode } });
  const [showFloater] = React.useState(false);
  const [, setAmount] = React.useState(0);
  const [isBannerVisible, setIsBannerVisible] = React.useState(false);
  const [, setIsMobile] = React.useState(true); // Default to true to avoid flash

  // ✅ ADD STATE FOR SOURCE TRACKING
  const [, setAdSource] = React.useState<string | null>(null);
  const [isIOSDevice, setIsIOSDevice] = React.useState(true);

  const toggleColorMode = () => {
    setMode((prev) => (prev === "dark" ? "light" : "dark"));
  };

  const handleBannerStateChange = (isVisible: boolean) => {
    setIsBannerVisible(isVisible);
  };

  React.useEffect(() => {
    // Check if device is mobile (max width 768px for phones only)
    const checkMobile = () => {
      const isMobileDevice = window.innerWidth <= 768 &&
        /Android|webOS|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      setIsMobile(isMobileDevice);
    };

    const checkIOS = () => {
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
      if (!isIOS) {
        setIsIOSDevice(false);
      }
    };
    checkIOS();

    checkMobile();
    window.addEventListener('resize', checkMobile);

    // const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    // if (isIOS) {
    //   window.location.href = "/ios";
    //   return;
    // }

    const currentUrl = window.location.pathname;
    const match = currentUrl.match(/\/(\d+)\/?$/);
    const number = match ? parseInt(match[1], 10) : 0;
    setAmount(number);

    // Redirect iOS users to /ios if they are not already there
    // This handles the case where this app might be mounted at root
    const isIOSDeviceCheck = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    if (isIOSDeviceCheck && !currentUrl.includes('/ios') && !window.location.host.includes('localhost') && !window.location.host.includes('ngrok-free.dev')) {
      // Only redirect if explicitly not on an ios path (approximate check)
      // And avoid redirect loop if we are actually the ios app just served at specific route
      // Ideally, we redirect to 'https://app20.in/ios'
      window.location.href = "https://app20.in/ios";
    }

    // ✅ CAPTURE SOURCE PARAMETER FROM URL
    const urlParams = new URLSearchParams(window.location.search);
    const source = urlParams.get('source')?.toLowerCase() || urlParams.get('utm_source')?.toLowerCase();

    if (source) {
      setAdSource(source);
      // Store in sessionStorage to persist across page navigation
      sessionStorage.setItem('ad_source', source);
    } else {
      // Check if source was stored from previous page
      const storedSource = sessionStorage.getItem('ad_source');
      if (storedSource) {
        setAdSource(storedSource);
      }
    }

    // ✅ INSTALL RETURN LOGIC
    const checkInstallReturn = () => {
      const hasInitiatedInstall = localStorage.getItem('install_initiated');
      if (hasInitiatedInstall === 'true' && document.visibilityState === 'visible') {
        localStorage.removeItem('install_initiated');
        // Navigate everyone to task submission page first to collect data
        window.location.href = '/submit-task';
      }
    };

    // Check on mount
    checkInstallReturn();

    // Check on visibility change (when returning from Play Store)
    const handleVisibilityChange = () => {
      checkInstallReturn();
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    // ✅ CHECK FOR PERSISTENT SESSION (Endless Cookie)
    const isLoggedIn = localStorage.getItem('is_logged_in') === 'true';
    if (isLoggedIn) {
      // Redirect to dashboard if user is already logged in
      window.location.href = '/dashboard';
      return;
    }



    return () => {
      window.removeEventListener('resize', checkMobile);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);



  if (!isIOSDevice) {
    return (
      <ThemeProvider theme={defaultTheme}>
        <CssBaseline />
        <Box
          sx={{
            height: "100vh",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            bgcolor: "background.default",
            p: 3
          }}
        >
          <Typography variant="h5" color="text.primary" align="center" gutterBottom>
            iOS Device Required
          </Typography>
          <Typography variant="body1" color="text.secondary" align="center">
            This application is exclusively available for iOS devices. Please access it using an iPhone or iPad.
          </Typography>
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={showCustomTheme ? LPtheme : defaultTheme}>
      <CssBaseline />
      <Helmet>
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://app20.in/" />
      </Helmet>
      <AppAppBar
        mode={mode}
        toggleColorMode={toggleColorMode}
        isBannerVisible={isBannerVisible}
        transparentMode={true}
        useTextLogo={false}
        simpleNavbar={true}
      />
      <PayBanner onBannerStateChange={handleBannerStateChange} />
      <Hero />
      <Box sx={{ bgcolor: "background.default", marginTop: 0 }}>

        {/* How It Works Section */}
        <HowItWorks />

        {/* Task Steps */}
        <TaskSteps />

        {/* Replaced SecondarySec usage */}
        <Box sx={{ width: "100%", mt: 2, mb: 0 }}>
          <OfferSection isPublicPage={true} />
        </Box>

        <CounterSection />

        <Box sx={{ height: 32 }} />

        <Testimonials />

        {/* Combined FAQ and Footer Section with Yellow Gradient Background */}
        <Box
          sx={{
            background: "linear-gradient(180deg, #FFFFFF 0%, #FFFFFF 60%, #FFB300 100%)", // White top -> White mid -> Yellow Footer
            pt: 0,
            pb: 0,
            mt: 0
          }}
        >
          {/* Security Badges Section - Before FAQ */}
          <PartnersSection />

          <FAQ />
          {showFloater && <Floater />}
          <Footer />
        </Box>
      </Box>
    </ThemeProvider>
  );
}